import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { GuestCapacityCard } from "@wedding/guest-capacity";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2, Upload, Download, Pencil, Trash2, Plus,
  ShieldOff, ShieldAlert, Search, X, Check, Users,
} from "lucide-react";
import type { Diet } from "@wedding/shared";
import { MEDIA_DELETE_GRACE_DAYS } from "@wedding/shared";
import { ConfirmDialog } from "@wedding/ui";
import { useAdminApi } from "../hooks/use-admin-api";
import { useWedding } from "../contexts/wedding-context";
import { cn } from "../lib/utils";

// ─── Types ────────────────────────────────────────────────────
interface GuestRow {
  id: string;
  phone_e164: string;
  name: string | null;
  source?: "organizer" | "qr";
  created_at: string;
  banned_until: string | null;
  rsvp_status: string | null;
  headcount: number | null;
  diet: string | null;
  notes: string | null;
  guest_edited_by: string | null;
  rsvp_edited_by: string | null;
}

// ─── Diyet etiketleri ─────────────────────────────────────────
/**
 * Etiketi OLAN diyetler ("none" hariç — o rozet hiç gösterilmez).
 *
 * `Record<Exclude<Diet, "none">, true>` olduğu için `Diet` union'ı yeni bir üye
 * kazanırsa tsc BURAYI kırar → yeni diyet eklenip çevirisi unutulamaz.
 */
const DIET_LABELLED: Record<Exclude<Diet, "none">, true> = {
  vegan: true,
  vegetarian: true,
  gluten_free: true,
  other: true,
};
type LabelledDiet = keyof typeof DIET_LABELLED;

/** DB'de `diet` serbest TEXT (CHECK yok) → dar tipe geçmeden önce doğrula. */
const isLabelledDiet = (d: string | null | undefined): d is LabelledDiet =>
  !!d && Object.prototype.hasOwnProperty.call(DIET_LABELLED, d);

/**
 * `t(\`guests.diet_${d}\`)` yerine exhaustive switch.
 *
 * Neden: şablon çağrısını `pnpm check:i18n-keys` yalnız NAMESPACE seviyesinde
 * denetleyebiliyordu (`guests` var mı?) — `guests.diet_vegan` silinse sessiz
 * kalırdı. Her dal literal olduğu için artık TAM ANAHTAR seviyesinde denetlenir.
 * `default` dalı YOK: bir üye eklenirse tsc "lacks ending return" der.
 */
function dietLabel(t: (key: string) => string, diet: LabelledDiet): string {
  switch (diet) {
    case "vegan":       return t("guests.diet_vegan");
    case "vegetarian":  return t("guests.diet_vegetarian");
    case "gluten_free": return t("guests.diet_gluten_free");
    case "other":       return t("guests.diet_other");
  }
}

// ─── Constants ────────────────────────────────────────────────
const PAGE_SIZES = [10, 20, 50] as const;
type PageSize = typeof PAGE_SIZES[number];
const BAN_PERMANENT = "2099-12-31T23:59:59.000Z";

// ─── Helpers ─────────────────────────────────────────────────
function formatPhone(raw: string): string {
  let v = raw.replace(/[^\d+]/g, "");
  if (v && !v.startsWith("+")) v = "+" + v;
  v = "+" + v.slice(1).replace(/\+/g, "");
  return v.slice(0, 16);
}

function isValidE164(phone: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(phone);
}

function isBanned(g: GuestRow, nowIso: string): boolean {
  return !!g.banned_until && g.banned_until > nowIso;
}

function banRemainingLabel(bannedUntil: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (bannedUntil >= "9999") return "∞";
  const ms = new Date(bannedUntil).getTime() - Date.now();
  if (ms <= 0) return "";
  const totalMins = Math.ceil(ms / 60_000);
  if (totalMins < 60) return t("guests.timeMinutes", { count: totalMins });
  const totalHours = Math.ceil(ms / 3_600_000);
  if (totalHours < 24) return t("guests.timeHours", { count: totalHours });
  const days = Math.ceil(ms / 86_400_000);
  return t("guests.timeDays", { count: days });
}

function banUntilDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

// ─── Avatar helpers ───────────────────────────────────────────
const AVATAR_COLORS = [
  "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300",
  "bg-primary/10 text-primary",
  "bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300",
];

function getInitials(name: string | null): string {
  if (!name?.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name: string | null): string {
  if (!name) return "bg-muted text-muted-foreground";
  const hash = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// ─── Card shell ───────────────────────────────────────────────
const card = "bg-card dark:bg-[rgba(27,24,23,0.75)] dark:backdrop-blur-xl border border-border dark:border-primary/20 shadow-sm rounded-2xl overflow-hidden";

// ─── Shared input class ───────────────────────────────────────
const inputCls = "w-full rounded-xl border border-border bg-muted/30 px-3 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:bg-background transition-colors";

// ─── Components ───────────────────────────────────────────────
function GuestAvatar({ name }: { name: string | null }) {
  return (
    <div className={cn(
      "h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-bold",
      getAvatarColor(name)
    )}>
      {getInitials(name)}
    </div>
  );
}

function RsvpBadge({ status, t }: { status: string | null; t: (k: string) => string }) {
  if (!status) return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-muted/60 text-muted-foreground">
      —
    </span>
  );
  const map: Record<string, { label: string; cls: string }> = {
    yes:   { label: t("guests.rsvpYes"),   cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-inset ring-emerald-500/20" },
    no:    { label: t("guests.rsvpNo"),    cls: "bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-1 ring-inset ring-rose-500/20" },
    maybe: { label: t("guests.rsvpMaybe"), cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-1 ring-inset ring-amber-500/20" },
  };
  const cfg = map[status] ?? { label: status, cls: "bg-muted/60 text-muted-foreground" };
  return (
    <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold", cfg.cls)}>
      {cfg.label}
    </span>
  );
}

function HeadcountPill({ status, headcount, t }: { status: string | null; headcount: number | null; t: (k: string) => string }) {
  if ((status !== "yes" && status !== "maybe") || !headcount || headcount < 1) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/70 text-muted-foreground px-2 py-0.5 text-[11px] font-medium tabular-nums">
      <Users className="w-3 h-3 shrink-0" />
      {headcount} {t("guests.headcountPerson")}
    </span>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  return (
    <span className="flex flex-col leading-[0] gap-px ml-0.5">
      <span className={cn("text-[8px] leading-none", active && dir === "asc" ? "text-primary" : "text-muted-foreground/30")}>▲</span>
      <span className={cn("text-[8px] leading-none", active && dir === "desc" ? "text-primary" : "text-muted-foreground/30")}>▼</span>
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md rounded-2xl bg-card border border-border shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/60 shrink-0">
          <h3 className="text-base font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors cursor-pointer">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 space-y-4">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── Page ─────────────────────────────────────────────────────
export function GuestsPage() {
  const { t, i18n } = useTranslation();
  const api = useAdminApi();
  const { slug, wedding } = useWedding();

  const rsvpCfg: { showDietField?: boolean; showNotesField?: boolean; maxHeadcount?: number } = wedding?.config?.rsvp ?? {};
  const showDiet  = rsvpCfg.showDietField  !== false;
  const showNotes = rsvpCfg.showNotesField !== false;
  const maxHc     = rsvpCfg.maxHeadcount   ?? 10;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nowIso = useMemo(() => new Date().toISOString(), []);

  // ─── Toast ──────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  }, []);

  // ─── Data ────────────────────────────────────────────────
  const [guests, setGuests] = useState<GuestRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pageSize, setPageSize] = useState<PageSize>(10);

  // ─── Filters, search & sort ───────────────────────────────
  const [rsvpFilter, setRsvpFilter] = useState<"all" | "yes" | "no" | "maybe" | "none">("all");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  type SortKey = "name" | "rsvp" | "date";
  type SortDir = "asc" | "desc";
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const buildParams = useCallback((overrides: { limit?: PageSize; search?: string; rsvp?: string; sort?: SortKey; dir?: SortDir; cursor?: string } = {}) => {
    const limit  = overrides.limit  ?? pageSize;
    const search = overrides.search !== undefined ? overrides.search : activeSearch;
    const rsvp   = overrides.rsvp   !== undefined ? overrides.rsvp   : rsvpFilter;
    const sort   = overrides.sort   ?? sortKey;
    const dir    = overrides.dir    ?? sortDir;
    const params = new URLSearchParams({ limit: String(limit), sort, dir });
    if (search.trim()) params.set("search", search.trim());
    if (rsvp !== "all") params.set("rsvp", rsvp);
    if (overrides.cursor) params.set("cursor", overrides.cursor);
    return { params, limit };
  }, [pageSize, activeSearch, rsvpFilter, sortKey, sortDir]);

  const fetchGuests = useCallback(async (overrides: { limit?: PageSize; search?: string; rsvp?: string; sort?: SortKey; dir?: SortDir } = {}) => {
    const { params, limit } = buildParams(overrides);
    setIsLoading(true);
    try {
      const res = await api.get<GuestRow[]>(`/guests?${params}`);
      const rows = res.data ?? [];
      setGuests(rows);
      setHasMore(rows.length >= limit);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setIsLoading(false);
    }
  }, [api, buildParams, showToast, t]);

  const fetchMoreLoadingRef = useRef(false);
  const fetchMore = useCallback(async () => {
    if (fetchMoreLoadingRef.current || !guests.length) return;
    fetchMoreLoadingRef.current = true;
    setLoadingMore(true);
    try {
      const cursor = guests[guests.length - 1].created_at;
      const { params, limit } = buildParams({ cursor });
      const res = await api.get<GuestRow[]>(`/guests?${params}`);
      const rows = res.data ?? [];
      setGuests(prev => [...prev, ...rows]);
      setHasMore(rows.length >= limit);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      fetchMoreLoadingRef.current = false;
      setLoadingMore(false);
    }
  }, [api, buildParams, guests, showToast, t]);

  useEffect(() => { fetchGuests(); }, [fetchGuests]);

  function toggleSort(key: SortKey) {
    const newDir: SortDir = sortKey === key ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(key);
    setSortDir(newDir);
    fetchGuests({ sort: key, dir: newDir });
  }

  const filteredGuests = useMemo(
    () => guests.map(g => ({
      ...g,
      _formattedDate: new Date(g.created_at).toLocaleDateString(i18n.language),
    })),
    [guests, i18n.language]
  );

  // ─── Add guest ───────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPhone, setAddPhone] = useState("");
  const [addName, setAddName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  function closeAddModal() {
    setShowAddModal(false);
    setAddPhone("");
    setAddName("");
    setAddError(null);
  }

  async function handleAddGuest(e: React.FormEvent) {
    e.preventDefault();
    if (!addName.trim()) { setAddError(t("guests.nameRequired")); return; }
    if (!isValidE164(addPhone)) { setAddError(t("guests.phoneInvalid")); return; }
    setAdding(true);
    setAddError(null);
    try {
      await api.post("/guests", { phone: addPhone, name: addName.trim() });
      await fetchGuests();
      closeAddModal();
      showToast(t("guests.added"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.unknownError");
      setAddError(msg);
      showToast(msg, false);
    } finally {
      setAdding(false);
    }
  }

  // ─── Edit guest ──────────────────────────────────────────
  const [editingGuest, setEditingGuest] = useState<GuestRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editRsvp, setEditRsvp] = useState("");
  const [editHeadcount, setEditHeadcount] = useState(1);
  const [editDiet, setEditDiet] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);

  function openEdit(g: GuestRow) {
    setEditingGuest(g);
    setEditName(g.name ?? "");
    setEditPhone(g.phone_e164);
    setEditRsvp(g.rsvp_status ?? "");
    setEditHeadcount(g.headcount ?? 1);
    setEditDiet(g.diet ?? "");
    setEditNotes(g.notes ?? "");
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingGuest) return;
    if (!isValidE164(editPhone)) { showToast(t("guests.phoneInvalid"), false); return; }
    setSaving(true);
    try {
      await api.patch(`/guests/${editingGuest.id}`, { name: editName, phone: editPhone });
      if (editRsvp) {
        await api.patch(`/rsvp/${editingGuest.id}`, {
          status: editRsvp,
          headcount: editHeadcount,
          diet: editDiet || undefined,
        });
      } else {
        await api.delete(`/rsvp/${editingGuest.id}`);
      }
      await fetchGuests();
      setEditingGuest(null);
      showToast(t("guests.saved"));
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.unknownError"), false);
    } finally {
      setSaving(false);
    }
  }

  // ─── Delete ──────────────────────────────────────────────
  // Misafiri silmek fotoğraflarını da silmez — ayrı karar, diyalogda sorulur.
  const [deleteTarget, setDeleteTarget] = useState<
    { id: string; name: string | null; mediaCount: number } | null
  >(null);
  const [deleteMediaToo, setDeleteMediaToo] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete(id: string, name: string | null) {
    setDeleteMediaToo(false);
    let mediaCount = 0;
    try {
      const res = await api.get<never>(`/guests/${id}/media-count`);
      mediaCount = (res as unknown as { count?: number }).count ?? 0;
    } catch { /* sayaç kritik değil */ }
    setDeleteTarget({ id, name, mediaCount });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/guests/${deleteTarget.id}${deleteMediaToo ? "?deleteMedia=1" : ""}`);
      setGuests(prev => prev.filter(g => g.id !== deleteTarget.id));
      showToast(t("guests.deleted"));
      setDeleteTarget(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.unknownError"), false);
    } finally {
      setDeleting(false);
    }
  }

  // ─── Ban ─────────────────────────────────────────────────
  const [banMenu, setBanMenu] = useState<{ id: string; rect: DOMRect } | null>(null);
  const [banReason, setBanReason] = useState("");

  useEffect(() => {
    if (!banMenu) return;
    const handler = () => setBanMenu(null);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [banMenu]);

  async function handleBan(id: string, until: string | null) {
    const reason = banReason.trim() || undefined;
    setBanMenu(null);
    setBanReason("");
    try {
      await api.patch(`/guests/${id}/ban`, { until, reason });
      setGuests(prev => prev.map(g => g.id === id ? { ...g, banned_until: until } : g));
      showToast(t(until ? "guests.guestBanned" : "guests.guestUnbanned"));
    } catch {
      showToast(t("common.error"), false);
    }
  }

  // ─── Import / Export ─────────────────────────────────────
  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const list = Array.isArray(json) ? json : json.guests;
      if (!Array.isArray(list)) throw new Error(t("guests.importJsonError"));
      const res = await api.post<{ imported: number; skipped: number; total: number }>("/guests/import", list);
      const r = res.data!;
      showToast(t("guests.importSuccess", { imported: r.imported, skipped: r.skipped, total: r.total }));
      await fetchGuests();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.unknownError"), false);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleExport() {
    const data = guests.map(g => ({ phone: g.phone_e164, name: g.name ?? undefined }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `guests-${slug}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(t("guests.exported", { count: guests.length }));
  }

  // ─── Loading skeleton ────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="h-8 w-52 rounded-xl bg-muted animate-pulse" />
          <div className="flex gap-2">
            <div className="h-9 w-24 rounded-full bg-muted animate-pulse" />
            <div className="h-9 w-24 rounded-full bg-muted animate-pulse" />
            <div className="h-9 w-32 rounded-full bg-muted animate-pulse" />
          </div>
        </div>
        <div className="flex gap-2">
          {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-8 w-20 rounded-full bg-muted animate-pulse" />)}
        </div>
        <div className={cn(card, "h-72 animate-pulse bg-muted")} />
      </div>
    );
  }

  // ─── Filter tabs config ──────────────────────────────────
  const filterTabs: { key: typeof rsvpFilter; label: string }[] = [
    { key: "all",   label: t("guests.filterAll") },
    { key: "yes",   label: t("guests.rsvpYes") },
    { key: "maybe", label: t("guests.rsvpMaybe") },
    { key: "no",    label: t("guests.rsvpNo") },
    { key: "none",  label: t("guests.filterNone") },
  ];

  // ─── Shared action buttons ───────────────────────────────
  function ActionButtons({ g }: { g: GuestRow & { _formattedDate: string } }) {
    return (
      <>
        <button
          onClick={() => openEdit(g)}
          className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          title={t("common.edit")}
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={e => {
            e.stopPropagation();
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setBanMenu(banMenu?.id === g.id ? null : { id: g.id, rect });
          }}
          className={cn(
            "p-2 rounded-full transition-colors cursor-pointer",
            isBanned(g, nowIso)
              ? "text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-500/10"
              : "text-muted-foreground hover:bg-muted hover:text-amber-600"
          )}
          title={
            isBanned(g, nowIso)
              ? g.banned_until! >= "9999"
                ? `${t("guests.banPermanentLabel")} — ${t("guests.unban")}`
                : `${t("guests.banUntil", { date: new Date(g.banned_until!).toLocaleDateString() })} (${banRemainingLabel(g.banned_until!, t)} ${t("common.left")}) — ${t("guests.unban")}`
              : t("guests.banGuest")
          }
        >
          {isBanned(g, nowIso) ? <ShieldOff className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
        </button>
        <button
          onClick={() => handleDelete(g.id, g.name)}
          className="p-2 rounded-full text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 transition-colors cursor-pointer"
          title={t("common.delete")}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </>
    );
  }

  const emptyNode = (
    <div className="py-16 text-center">
      <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
      <p className="text-sm text-muted-foreground">
        {activeSearch || rsvpFilter !== "all" ? t("guests.noGuestsSearch") : t("guests.noGuests")}
      </p>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* ── Misafir kotası: liste + QR toplamı paket limitine bağlı (2026-10-15) ── */}
      <GuestCapacityCard apiBase={`/api/admin/w/${slug}`} role="admin" />

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-bold leading-tight">
          {t("guests.title")}
          <span className="ml-2 text-lg font-normal text-muted-foreground font-sans">({guests.length})</span>
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted hover:border-primary/30 transition-colors cursor-pointer disabled:opacity-50"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span className="hidden sm:inline">{t("guests.importBtn")}</span>
          </button>
          <button
            onClick={handleExport}
            disabled={guests.length === 0}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted hover:border-primary/30 transition-colors cursor-pointer disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t("guests.exportBtn")}</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 cursor-pointer"
          >
            <Plus className="h-4 w-4" />
            {t("guests.addGuest")}
          </button>
        </div>
      </div>

      {/* ── Filters + Search ── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-2 flex-wrap">
          {filterTabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setRsvpFilter(key); fetchGuests({ rsvp: key }); }}
              className={cn(
                "px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer",
                rsvpFilter === key
                  ? "bg-primary text-white shadow-sm shadow-primary/20"
                  : "bg-card border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[180px] sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={searchInput}
            onChange={e => {
              setSearchInput(e.target.value);
              if (!e.target.value) {
                setActiveSearch("");
                fetchGuests({ search: "" });
              }
            }}
            onKeyDown={e => {
              if (e.key === "Enter") {
                setActiveSearch(searchInput);
                fetchGuests({ search: searchInput });
              }
              if (e.key === "Escape") {
                setSearchInput("");
                setActiveSearch("");
                fetchGuests({ search: "" });
              }
            }}
            placeholder={t("guests.searchPlaceholder")}
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-border bg-card text-sm focus:outline-none focus:border-primary/50 transition-colors"
          />
        </div>
      </div>

      {/* ── Desktop table ── */}
      <div className={cn(card, "hidden md:block")}>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30">
              <th className="px-6 py-3.5 text-left">
                <button onClick={() => toggleSort("name")} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  {t("guests.columnName")}
                  <SortIcon active={sortKey === "name"} dir={sortDir} />
                </button>
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {t("guests.columnPhone")}
              </th>
              <th className="px-6 py-3.5 text-center">
                <button onClick={() => toggleSort("rsvp")} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  {t("guests.columnRsvp")}
                  <SortIcon active={sortKey === "rsvp"} dir={sortDir} />
                </button>
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {t("guests.columnDetails")}
              </th>
              <th className="px-6 py-3.5 w-36" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {filteredGuests.length === 0 ? (
              <tr>
                <td colSpan={5}>{emptyNode}</td>
              </tr>
            ) : (
              filteredGuests.map(g => (
                <tr key={g.id} className="hover:bg-primary/5 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <GuestAvatar name={g.name} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold truncate">
                            {g.name ?? <span className="text-muted-foreground italic text-xs font-normal">{t("guests.noName")}</span>}
                          </p>
                          {g.source === "qr" && (
                            <span className="rounded-full bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 px-1.5 py-0.5 text-[10px] font-bold">QR</span>
                          )}
                          {isBanned(g, nowIso) && (
                            <span className="rounded-full bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 text-[10px] font-bold">
                              🚫 {banRemainingLabel(g.banned_until!, t)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <p className="text-[11px] text-muted-foreground">{g._formattedDate}</p>
                          <HeadcountPill status={g.rsvp_status} headcount={g.headcount} t={t} />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-muted-foreground whitespace-nowrap">
                    {g.phone_e164}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <RsvpBadge status={g.rsvp_status} t={t} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {showDiet && isLabelledDiet(g.diet) && (
                        <span className="inline-flex items-center rounded-full bg-violet-100 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 px-2 py-0.5 text-[10px] font-semibold">
                          {dietLabel(t, g.diet)}
                        </span>
                      )}
                      {showNotes && g.notes && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold max-w-[140px] truncate"
                          title={g.notes}
                        >
                          {g.notes}
                        </span>
                      )}
                      {(!showDiet || !g.diet || g.diet === "none") && (!showNotes || !g.notes) && (
                        <span className="text-xs text-muted-foreground/50">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-0.5">
                      <ActionButtons g={g} />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Mobile card list ── */}
      <div className="md:hidden space-y-2">
        {filteredGuests.length === 0 ? (
          <div className={card}>{emptyNode}</div>
        ) : (
          filteredGuests.map(g => (
            <div key={g.id} className={cn(card, "p-4")}>
              <div className="flex items-center gap-3">
                <GuestAvatar name={g.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-semibold text-sm truncate">
                      {g.name ?? <span className="text-muted-foreground italic text-xs font-normal">{t("guests.noName")}</span>}
                    </p>
                    {g.source === "qr" && (
                      <span className="rounded-full bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 px-1.5 py-0.5 text-[10px] font-bold">QR</span>
                    )}
                    {isBanned(g, nowIso) && (
                      <span className="rounded-full bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 text-[10px] font-bold">{t("guests.banned")}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{g.phone_e164}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <HeadcountPill status={g.rsvp_status} headcount={g.headcount} t={t} />
                    {showDiet && isLabelledDiet(g.diet) && (
                      <span className="rounded-full bg-violet-100 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 px-1.5 py-0.5 text-[10px] font-semibold">
                        {dietLabel(t, g.diet)}
                      </span>
                    )}
                    {showNotes && g.notes && (
                      <span className="rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 text-[10px] font-semibold max-w-[160px] truncate">
                        {g.notes}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  <RsvpBadge status={g.rsvp_status} t={t} />
                </div>
              </div>
              <div className="flex items-center justify-end gap-0.5 mt-2.5 pt-2.5 border-t border-border/40">
                <ActionButtons g={g} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Load more + page size ── */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-2">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {PAGE_SIZES.map(s => (
            <button
              key={s}
              onClick={() => { setPageSize(s); fetchGuests({ limit: s }); }}
              className={cn(
                "px-3 py-1 rounded-full font-medium transition-colors cursor-pointer",
                pageSize === s ? "bg-primary/10 text-primary" : "hover:bg-muted text-muted-foreground"
              )}
            >
              {s}
            </button>
          ))}
          <span className="ml-1">{t("common.perPage")}</span>
        </div>
        {hasMore && (
          <button
            onClick={fetchMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
          >
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("common.loadMore")}
          </button>
        )}
      </div>

      {/* ── Add Guest Modal ── */}
      {showAddModal && (
        <Modal title={t("guests.addGuest")} onClose={closeAddModal}>
          <form onSubmit={handleAddGuest} className="space-y-4">
            <Field label={t("guests.phoneRequired")}>
              <input
                type="tel"
                inputMode="tel"
                value={addPhone}
                onChange={e => { setAddPhone(formatPhone(e.target.value)); setAddError(null); }}
                placeholder="+905551234567"
                required
                autoFocus
                className={cn(
                  inputCls,
                  addPhone.length > 2 && (isValidE164(addPhone) ? "border-emerald-500" : "border-rose-400")
                )}
              />
              <p className="text-xs text-muted-foreground">{t("guests.phoneHint")}</p>
              {addError && <p className="text-xs text-rose-600">{addError}</p>}
            </Field>
            <Field label={`${t("common.name")} *`}>
              <input
                type="text"
                value={addName}
                onChange={e => setAddName(e.target.value)}
                placeholder={t("guests.namePlaceholder")}
                required
                className={inputCls}
              />
            </Field>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={closeAddModal}
                className="flex-1 rounded-xl border border-border py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={adding || !addPhone.trim() || !addName.trim()}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {t("common.add")}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Edit Guest Modal ── */}
      {editingGuest && (
        <Modal title={t("guests.editTitle")} onClose={() => setEditingGuest(null)}>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <Field label={t("common.name")}>
              <input
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className={inputCls}
                autoFocus
              />
            </Field>
            <Field label={t("guests.columnPhone")}>
              <input
                type="tel"
                inputMode="tel"
                value={editPhone}
                onChange={e => setEditPhone(formatPhone(e.target.value))}
                className={cn(
                  inputCls,
                  editPhone.length > 2 && (isValidE164(editPhone) ? "border-emerald-500" : "border-rose-400")
                )}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("guests.columnRsvp")}>
                <select
                  value={editRsvp}
                  onChange={e => setEditRsvp(e.target.value)}
                  className={inputCls}
                >
                  <option value="">—</option>
                  <option value="yes">{t("guests.rsvpYes")}</option>
                  <option value="maybe">{t("guests.rsvpMaybe")}</option>
                  <option value="no">{t("guests.rsvpNo")}</option>
                </select>
              </Field>
              <Field label={t("guests.columnHeadcount")}>
                <input
                  type="number"
                  min={1}
                  max={maxHc}
                  value={editHeadcount}
                  onChange={e => setEditHeadcount(parseInt(e.target.value) || 1)}
                  className={inputCls}
                />
                <p className="text-[10px] text-muted-foreground">{t("guests.headcountMax", { max: maxHc })}</p>
              </Field>
            </div>
            {showDiet && isLabelledDiet(editDiet) && (
              <Field label={t("guests.columnDiet")}>
                <div className="rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-sm text-muted-foreground">
                  {dietLabel(t, editDiet)}
                </div>
              </Field>
            )}
            {showNotes && editNotes && (
              <Field label={t("guests.columnNotes")}>
                <div className="rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-sm text-muted-foreground whitespace-pre-wrap break-words min-h-[4rem]">
                  {editNotes}
                </div>
              </Field>
            )}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditingGuest(null)}
                className="flex-1 rounded-xl border border-border py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {t("common.save")}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Toast ── */}
      {/* ── Misafir silme onayı (medya kararı ayrı) ── */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={t("guests.deleteConfirm", { name: deleteTarget?.name ?? deleteTarget?.id.slice(0, 8) ?? "" })}
        description={
          deleteTarget && deleteTarget.mediaCount > 0
            ? t("guests.deleteMediaKept", { count: deleteTarget.mediaCount })
            : t("guests.deleteNoMedia")
        }
        option={
          deleteTarget && deleteTarget.mediaCount > 0
            ? {
                checked: deleteMediaToo,
                onChange: setDeleteMediaToo,
                label: t("guests.deleteMediaToo", { count: deleteTarget.mediaCount }),
                hint: t("guests.deleteMediaTooHint", { days: MEDIA_DELETE_GRACE_DAYS }),
              }
            : undefined
        }
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {toast && (
        <div className={cn(
          "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-lg pointer-events-none",
          toast.ok ? "bg-emerald-600" : "bg-rose-600"
        )}>
          {toast.msg}
        </div>
      )}

      {/* ── Ban dropdown ── */}
      {banMenu && (() => {
        const g = guests.find(x => x.id === banMenu.id);
        if (!g) return null;
        const right = window.innerWidth - banMenu.rect.right;
        return createPortal(
          <div
            style={{ position: "fixed", top: banMenu.rect.bottom + 6, right }}
            className="z-[9999] min-w-[152px] rounded-xl border border-border bg-card shadow-xl py-1.5"
            onMouseDown={e => e.stopPropagation()}
          >
            {isBanned(g, nowIso) ? (
              <>
                <div className="px-4 py-1.5 text-[11px] text-muted-foreground border-b border-border/40 mb-1">
                  {g.banned_until! >= "9999"
                    ? t("guests.banPermanentLabel")
                    : t("guests.banUntil", { date: new Date(g.banned_until!).toLocaleDateString() })}
                  {g.banned_until! < "9999" && (
                    <span className="ml-1 text-orange-500 font-semibold">({banRemainingLabel(g.banned_until!, t)} {t("common.left")})</span>
                  )}
                </div>
                <button
                  onClick={() => handleBan(g.id, null)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-emerald-600 hover:bg-primary/5 cursor-pointer"
                >
                  <ShieldOff className="h-3.5 w-3.5" />
                  {t("guests.unban")}
                </button>
              </>
            ) : (
              <>
                <div className="px-3 pt-2 pb-1.5">
                  <input
                    type="text"
                    value={banReason}
                    onChange={e => setBanReason(e.target.value)}
                    placeholder={t("guests.banReasonPlaceholder")}
                    maxLength={200}
                    className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div className="border-t border-border/40 mt-1" />
                <button onClick={() => handleBan(g.id, banUntilDate(1))} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-primary/5 cursor-pointer">{t("guests.banDuration1d")}</button>
                <button onClick={() => handleBan(g.id, banUntilDate(7))} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-primary/5 cursor-pointer">{t("guests.banDuration7d")}</button>
                <button onClick={() => handleBan(g.id, banUntilDate(30))} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-primary/5 cursor-pointer">{t("guests.banDuration30d")}</button>
                <div className="my-1 border-t border-border/40" />
                <button onClick={() => handleBan(g.id, BAN_PERMANENT)} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 cursor-pointer">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {t("guests.banPermanent")}
                </button>
              </>
            )}
          </div>,
          document.body
        );
      })()}
    </div>
  );
}
